/**
 * Pre-React URL bootstrap.
 *
 * Imported as the FIRST line of main.tsx, before supabase-js initializes
 * its client and before React Router reads the URL.
 *
 * Why: Supabase auth flows redirect to Site URL (the app root) by default,
 * not to /auth/callback. Magic-link sign-in (signInWithOtp) we control —
 * it sends emailRedirectTo=/auth/callback. But Dashboard "Invite user" and
 * password-recovery emails redirect to Site URL with auth params attached
 * in the query or hash. If we let those land on "/" the GatePage renders,
 * the user clicks "Business", ProtectedRoute sees no session and bounces
 * them to /login, which sends another magic link — a loop.
 *
 * The forms we may see:
 *   - ?code=X                          PKCE code from any flow
 *   - ?token_hash=X&type=invite|...    server-initiated PKCE with new email template
 *   - #access_token=X&refresh_token=Y  implicit / hash-based fragment
 *   - ?error_description=...           Supabase reports a failure
 *
 * detectSessionInUrl + PKCE in supabase-js auto-handles ?code= and
 * #access_token=, but only if the client initializes after the URL is
 * settled. By rewriting first we also let AuthCallbackPage take over
 * ?token_hash= via an explicit verifyOtp call.
 */

const AUTH_QUERY_KEYS = ['code', 'token_hash', 'error', 'error_description'] as const;

function looksLikeAuthRedirect(url: URL): boolean {
  for (const key of AUTH_QUERY_KEYS) {
    if (url.searchParams.has(key)) return true;
  }
  if (url.hash.includes('access_token=') || url.hash.includes('refresh_token=')) {
    return true;
  }
  return false;
}

function rewriteToCallback(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  if (url.pathname === '/auth/callback') return;
  if (!looksLikeAuthRedirect(url)) return;

  url.pathname = '/auth/callback';
  window.history.replaceState(null, '', url.toString());
}

rewriteToCallback();
